import { useCallback, useContext } from 'react';

import AuthContext from '../context/authContext';
import useHttp from './useHttp';

const BASE_URL = '/api/v1/employeeFreeDrinks';

// All API communication for the employee free drinks (Freigetränke) feature.
const useEmployeeFreeDrinks = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  // Hardcoded quota configuration (groups, drinks, quotas, period).
  const getConfig = useCallback(
    async () =>
      sendRequest({
        url: `${BASE_URL}/config`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      }),
    [authCtx, sendRequest]
  );

  // Own consumption for the current period, remaining quota per group and assigned branches.
  const getMyConsumption = useCallback(
    async () =>
      sendRequest({
        url: `${BASE_URL}/me`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      }),
    [authCtx, sendRequest]
  );

  // Log a consumed free drink. Throws on quota/branch errors so the caller can show a toast.
  const logDrink = useCallback(
    async (drinkKey, branchId) =>
      sendRequest({
        url: BASE_URL,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { drinkKey, branchId },
      }),
    [authCtx, sendRequest]
  );

  // Remove one of the user's own entries from the current period.
  const deleteEntry = useCallback(
    async id =>
      sendRequest({
        url: `${BASE_URL}/${id}`,
        method: 'DELETE',
        headers: authCtx.getAuthHeaders(),
      }),
    [authCtx, sendRequest]
  );

  // Aggregated statistics for a date range and optional branch filter (special right).
  const getStatistics = useCallback(
    async ({ from, to, branchId } = {}) => {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (branchId) params.branchId = branchId;
      return sendRequest({
        url: `${BASE_URL}/statistics`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
      });
    },
    [authCtx, sendRequest]
  );

  // Download the statistics as CSV or Excel. Returns a Blob.
  const downloadExport = useCallback(
    async (format, { from, to, branchId } = {}) => {
      const params = { format };
      if (from) params.from = from;
      if (to) params.to = to;
      if (branchId) params.branchId = branchId;
      return sendRequest({
        url: `${BASE_URL}/statistics/export`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
        returnBlob: true,
      });
    },
    [authCtx, sendRequest]
  );

  return {
    isLoading,
    error,
    getConfig,
    getMyConsumption,
    logDrink,
    deleteEntry,
    getStatistics,
    downloadExport,
  };
};

export default useEmployeeFreeDrinks;
